// Generic rail (<carousel-rail>): the scrolling, snapping and swiping
// are native; this only wires the arrows and keeps them disabled at the
// ends. Meant to be shared by every rail of the theme.
//
// Two opt-in extras, off unless the markup asks for them:
// - data-loop: the arrows wrap around at the ends instead of disabling;
// - data-autoplay="seconds": steps forward on its own, looping. Only while
//   the rail is on screen, never with reduced motion, and it holds while
//   the pointer is over it, focus is inside it or a finger or wheel just
//   moved it, so it never fights the visitor.

class CarouselRail extends HTMLElement {
  connectedCallback() {
    this.rail = this.querySelector('[data-rail]');
    this.prev = this.querySelector('[data-prev]');
    this.next = this.querySelector('[data-next]');
    if (!this.rail) return;

    this.loop = this.hasAttribute('data-loop') || this.hasAttribute('data-autoplay');

    this.controller = new AbortController();
    const signal = this.controller.signal;

    this.prev?.addEventListener('click', () => this.scrollByPage(-1), { signal });
    this.next?.addEventListener('click', () => this.scrollByPage(1), { signal });
    this.rail.addEventListener('scroll', () => this.syncArrows(), { passive: true, signal });
    window.addEventListener('resize', () => this.syncArrows(), { passive: true, signal });

    // The rail is measured again whenever it changes size and once the page
    // finishes loading: at connect time the cards may not be laid out yet,
    // and the arrows would start disabled with nowhere to scroll.
    this.observer = new ResizeObserver(() => this.syncArrows());
    this.observer.observe(this.rail);
    if (document.readyState !== 'complete') {
      window.addEventListener('load', () => this.syncArrows(), { once: true, signal });
    }

    this.syncArrows();
    this.startAutoplay(signal);
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    this.visibility?.disconnect();
    clearInterval(this.timer);
    cancelAnimationFrame(this.frame);
  }

  // Advances whole cards, never a raw viewport width: landing between two
  // snap points would make mandatory snapping pull the rail somewhere else.
  scrollByPage(direction) {
    const first = this.rail.firstElementChild;
    if (!first) return;

    // Reduced motion jumps straight to the next page instead of gliding.
    // 'instant', not 'auto': 'auto' follows the CSS scroll-behavior, and the
    // rails set it to smooth, so 'auto' still glides.
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';

    // Looping, the step past an end goes around to the other end.
    const max = this.rail.scrollWidth - this.rail.clientWidth;
    if (this.loop && direction > 0 && this.rail.scrollLeft >= max - 1) {
      this.rail.scrollTo({ left: 0, behavior });
      return;
    }
    if (this.loop && direction < 0 && this.rail.scrollLeft <= 1) {
      this.rail.scrollTo({ left: max, behavior });
      return;
    }

    const gap = parseFloat(getComputedStyle(this.rail).columnGap) || 0;
    const step = first.getBoundingClientRect().width + gap;
    // data-step="card" walks one card per click: a rail that highlights
    // the centre card moves the highlight, not a whole page.
    const perPage = this.dataset.step === 'card' ? 1 : Math.max(1, Math.floor(this.rail.clientWidth / step));
    this.rail.scrollBy({ left: direction * perPage * step, behavior });
  }

  startAutoplay(signal) {
    const seconds = Number(this.dataset.autoplay);
    if (!seconds || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const interval = seconds * 1000;

    // A touch, a wheel or an arrow restarts the wait, so the rail never
    // moves right after the visitor moved it.
    const touched = () => { this.touchedAt = Date.now(); };
    this.rail.addEventListener('pointerdown', touched, { passive: true, signal });
    this.rail.addEventListener('wheel', touched, { passive: true, signal });
    this.prev?.addEventListener('click', touched, { signal });
    this.next?.addEventListener('click', touched, { signal });

    // Off screen (a closed drawer, a rail scrolled away) it stays still.
    this.visibility = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
    });
    this.visibility.observe(this);

    this.timer = setInterval(() => {
      if (!this.visible || this.matches(':hover, :focus-within')) return;
      if (Date.now() - (this.touchedAt || 0) < interval) return;
      this.scrollByPage(1);
    }, interval);
  }

  syncArrows() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      const max = this.rail.scrollWidth - this.rail.clientWidth;
      // A rail that fits has no arrows to offer. The CSS reserves their
      // space from the first paint and only flips visibility, so revealing
      // or hiding them never shifts the layout.
      this.toggleAttribute('overflowing', max > 1);
      // Looping, there is always somewhere to go.
      if (this.prev) this.prev.disabled = !this.loop && this.rail.scrollLeft <= 1;
      if (this.next) this.next.disabled = !this.loop && this.rail.scrollLeft >= max - 1;
    });
  }
}

if (!customElements.get('carousel-rail')) {
  customElements.define('carousel-rail', CarouselRail);
}
